import React from "react";
import { Helmet } from "react-helmet-async";
import MainLayout from "../../layouts/MainLayout";

const features = [
  {
    n: "01",
    tag: "Speed",
    headline: "Minutes, not days.",
    body: "Whole documents — up to 200 pages — translated in under fifteen minutes. The time you used to lose to outsourcing: gone. Upload. Translate. That's it.",
  },
  {
    n: "02",
    tag: "Accuracy",
    headline: "Tuned for the language of the law.",
    body: "Trained on Sri Lankan legal terminology across Sinhala, Tamil, and English — pleadings, judgements, statutes, correspondence. Cause of Action stays Cause of Action. Subject to Costs stays Subject to Costs. Terms of art hold their register; the bench reads exactly what the bench expects.",
  },
  {
    n: "03",
    tag: "Structure",
    headline: "Every paragraph number, exactly where it was.",
    body: "Numbered paragraphs, sub-clauses, exhibits, recitals — preserved as written. When the source plaint refers to ¶ 06 and ¶ 12, the translation refers to ¶ 06 and ¶ 12. Exhibits marked P-01A, P-02A, P-03A remain P-01A, P-02A, P-03A — not renumbered, not re-ordered.",
  },
  {
    n: "04",
    tag: "Annotation",
    headline: "In-line glosses for terms of art.",
    body: "Where a Sinhala or Tamil term carries weight that resists translation, the original appears alongside the rendered text — quietly, in parentheses, in its own script. Witness (සාක්‍ෂිකරු). Judgment (තීන්දුව). Cause of Action (නඩු නිමිත්තක්). Meaning is never quietly lost in transit.",
  },
  {
    n: "05",
    tag: "Tracking",
    headline: "Source page numbers are tracked.",
    body: "Every translated section carries its source page reference — [Page 2 / 21], [Page 3 / 21] — line for line. When the bench cites p. 17, you can answer in seconds, not after a re-read of the whole bundle.",
  },
];

const Translate = () => {
  return (
    <MainLayout>
      <Helmet>
        <title>Legal Translation in Minutes — paralegal.lk</title>
        <meta
          name="description"
          content="A tri-lingual legal translator purpose built for Sri Lanka. Translate in minutes, not days. With paragraph numbers, source page references, and terms of art preserved in-line."
        />
        <link rel="canonical" href="https://www.paralegal.lk/translate" />
      </Helmet>

      <article className="translate-page w-full max-w-[1080px] mx-auto px-6 md:px-12 py-10 md:py-16 text-pl-text-primary">
        {/* Masthead row */}
        <header className="flex flex-wrap justify-between items-baseline gap-y-2 pb-4 mb-12 md:mb-16 border-b border-pl-border font-mono text-10 md:text-11 uppercase tracking-[0.22em] text-pl-text-tertiary">
          <div className="flex items-baseline gap-3">
            <span className="text-pl-accent">¶</span>
            <span>translate.paralegal.lk</span>
          </div>
          <div className="flex items-baseline gap-5">
            <span className="hidden sm:inline">EN · සිංහල · தமிழ்</span>
            <span>p. 01</span>
          </div>
        </header>

        {/* Trilingual flourish */}
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-8 reveal r-0">
          <span className="font-display italic text-pl-text-tertiary text-15 md:text-16">Translation</span>
          <span className="text-pl-accent-muted">·</span>
          <span className="font-sinhala text-pl-text-tertiary text-16 md:text-18">පරිවර්තනය</span>
          <span className="text-pl-accent-muted">·</span>
          <span className="font-tamil text-pl-text-tertiary text-16 md:text-18">மொழிபெயர்ப்பு</span>
        </div>

        {/* Hero */}
        <h1 className="font-display text-pl-text-primary leading-[1.02] tracking-[-0.015em] text-[42px] sm:text-[56px] md:text-[78px] lg:text-[88px] mb-8 md:mb-10 reveal r-1">
          Legal translation<br />
          in <em className="italic text-pl-accent">minutes</em>,<br />
          not in days.
        </h1>

        <p className="font-display text-18 md:text-22 leading-[1.55] text-pl-text-secondary max-w-[44ch] mb-12 md:mb-14 reveal r-2">
          A high-accuracy tri-lingual legal translator purpose built for Sri Lanka. Every paragraph number kept, every source page tracked, every term of art glossed in script. 
        </p>

        {/* Race bars — speed comparison */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6 mb-12 md:mb-14 max-w-2xl reveal r-3">
          <div>
            <div className="flex justify-between items-baseline font-mono text-10 uppercase tracking-[0.2em] text-pl-text-tertiary mb-2">
              <span>Outsourced first pass</span>
              <span>~ 7–14 days</span>
            </div>
            <div className="h-[2px] bg-pl-border w-full overflow-hidden">
              <span className="block h-full bg-pl-text-tertiary fill-slow"></span>
            </div>
          </div>
          <div>
            <div className="flex justify-between items-baseline font-mono text-10 uppercase tracking-[0.2em] text-pl-accent mb-2">
              <span>translate.paralegal.lk</span>
              <span>~ 12 min</span>
            </div>
            <div className="h-[2px] bg-pl-accent-subtle w-full overflow-hidden">
              <span className="block h-full bg-pl-accent fill-fast"></span>
            </div>
          </div>
        </div>

        {/* Primary CTA */}
        <div className="flex flex-wrap items-center gap-x-8 gap-y-4 mb-24 md:mb-32 reveal r-4">
          <a
            href="https://translate.paralegal.lk"
            className="group inline-flex items-center gap-3 px-7 py-4 bg-pl-text-primary text-pl-bg rounded-pl font-mono text-11 md:text-12 uppercase tracking-[0.2em] hover:bg-pl-accent transition-colors duration-200"
          >
          Open translate
            <span className="transition-transform duration-200 group-hover:translate-x-1">→</span>
          </a>
          <span className="font-mono text-10 md:text-11 text-pl-text-tertiary uppercase tracking-[0.18em]">
            Rs. 2,000 free credits · then Rs. 0.25 / word
          </span>
        </div>

        {/* § I — Features */}
        <SectionLabel n="I" label="What it does" />

        <div className="space-y-14 md:space-y-20 mb-24 md:mb-32">
          {features.map((f) => (
            <FeatureArticle key={f.n} {...f} />
          ))}
        </div>

        {/* § II — Real excerpt */}
        <SectionLabel n="II" label="Excerpt, by way of example" />

        <Excerpt />

        {/* § III — Begin */}
        <SectionLabel n="III" label="Begin" />

        <div className="max-w-2xl">
          <h2 className="font-display text-28 md:text-36 leading-[1.15] text-pl-text-primary mb-5">
            Begin with your next file.
          </h2>
          <p className="text-15 md:text-16 text-pl-text-secondary leading-[1.75] mb-7">
            Upload a PDF, DOCX, or scanned image. Check the word count and estimate before you run it. The first Rs. 2,000 is on the house.
          </p>
          <a
            href="https://translate.paralegal.lk"
            className="group inline-flex items-center gap-3 px-7 py-4 bg-pl-accent text-white rounded-pl font-mono text-11 md:text-12 uppercase tracking-[0.2em] hover:bg-pl-accent-hover transition-colors duration-200"
          >
            Try Translate
            <span className="transition-transform duration-200 group-hover:translate-x-1">→</span>
          </a>
        </div>

        {/* Page-scoped styles */}
        <style>{`
          .translate-page .reveal { opacity: 0; transform: translateY(14px); animation: tp-revealUp .9s cubic-bezier(.2,.7,.2,1) forwards; }
          .translate-page .r-0 { animation-delay: .00s; }
          .translate-page .r-1 { animation-delay: .08s; }
          .translate-page .r-2 { animation-delay: .22s; }
          .translate-page .r-3 { animation-delay: .36s; }
          .translate-page .r-4 { animation-delay: .50s; }
          @keyframes tp-revealUp { to { opacity: 1; transform: translateY(0); } }

          .translate-page .fill-slow { width: 0; animation: tp-fillSlow 5.5s ease-out .8s forwards; }
          .translate-page .fill-fast { width: 0; animation: tp-fillFast 1.1s cubic-bezier(.3,.7,.2,1) .8s forwards; }
          @keyframes tp-fillSlow { to { width: 100%; } }
          @keyframes tp-fillFast { to { width: 100%; } }

          .translate-page h1 em { font-style: italic; }

          @media (prefers-reduced-motion: reduce) {
            .translate-page .reveal,
            .translate-page .fill-slow,
            .translate-page .fill-fast { animation: none; opacity: 1; width: 100%; transform: none; }
          }
        `}</style>
      </article>
    </MainLayout>
  );
};

const SectionLabel = ({ n, label }) => (
  <div className="relative border-t border-pl-border mt-12 mb-10 md:mb-14">
    <div className="absolute -top-[10px] left-0 bg-pl-bg pr-4 font-mono text-10 md:text-11 uppercase tracking-[0.22em] text-pl-text-tertiary flex items-baseline gap-3">
      <span className="text-pl-accent">§ {n}</span>
      <span>{label}</span>
    </div>
  </div>
);

const FeatureArticle = ({ n, tag, headline, body }) => (
  <section className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-y-3 gap-x-10 md:gap-x-16 items-start">
    <div className="font-mono uppercase tracking-[0.22em] pt-1 flex md:block items-baseline gap-3">
      <span className="text-11 text-pl-accent">¶ {n}</span>
      <span className="block text-pl-text-tertiary text-10 md:mt-2">{tag}</span>
    </div>
    <div>
      <h3 className="font-display text-24 md:text-32 leading-[1.18] text-pl-text-primary mb-4 max-w-[26ch] tracking-[-0.005em]">
        {headline}
      </h3>
      <p className="text-15 md:text-16 text-pl-text-secondary leading-[1.78] max-w-[58ch]">
        {body}
      </p>
    </div>
  </section>
);

const SrcTerm = ({ children }) => (
  <span className="font-sinhala text-pl-accent text-14 md:text-15"> ({children})</span>
);
const ParaRef = ({ children }) => (
  <span className="font-mono text-12 md:text-13 text-pl-accent bg-pl-accent-subtle px-1.5 py-0.5 rounded-sm">{children}</span>
);
const Exhibit = ({ children }) => (
  <span className="font-mono text-12 md:text-13 text-pl-accent border border-pl-accent-muted px-1.5 py-0.5 rounded-sm">{children}</span>
);
const ItemNum = ({ children }) => (
  <span className="font-mono text-pl-text-tertiary text-13 md:text-14 pt-1 shrink-0 w-7 tabular-nums">{children}</span>
);

const Excerpt = () => (
  <figure className="rounded-pl-lg border border-pl-border bg-pl-bg-elevated shadow-pl-md overflow-hidden">
    {/* Excerpt header */}
    <figcaption className="flex flex-wrap justify-between items-baseline gap-2 px-6 md:px-8 py-4 border-b border-pl-border font-mono text-10 md:text-11 uppercase tracking-[0.2em] text-pl-text-tertiary">
      <span>Civil Appellate · Defamation · LKR 500 m</span>
      <span>
        Source:{" "}
        <span className="text-pl-text-secondary">Sinhala</span> →{" "}
        <span className="text-pl-text-secondary">English</span>
      </span>
    </figcaption>

    {/* Two-column body */}
    <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)]">
      {/* Source */}
      <div className="border-b md:border-b-0 md:border-r border-pl-border p-6 md:p-8 bg-pl-bg-subtle">
        <div className="flex items-baseline justify-between mb-4 font-mono text-10 uppercase tracking-[0.2em] text-pl-text-tertiary">
          <span className="text-pl-accent">Source · සිංහල</span>
          <span>Page 2 / 21</span>
        </div>
        <img
          src="/assets/translate/source-page-02.jpg"
          alt="Source page in Sinhala — Mount Lavinia Civil Appellate High Court judgement, page 2 of 21"
          className="w-full rounded-pl border border-pl-border shadow-pl-sm bg-white sticky top-6"
          loading="lazy"
        />
      </div>

      {/* Translation — full page 2 */}
      <div className="p-6 md:p-8">
        <div className="flex items-baseline justify-between mb-5 font-mono text-10 uppercase tracking-[0.2em] text-pl-text-tertiary">
          <span className="text-pl-accent">Translation · English</span>
          <span>
            Page 2 / 21 <span className="text-pl-accent">✓</span>
          </span>
        </div>

        <p className="font-display text-14 md:text-15 leading-[1.8] text-pl-text-primary mb-4">
          The Parties<SrcTerm>පාර්ශ්වකරුවන්</SrcTerm> expressed their consent on 2012.09.26, and from then on the Case<SrcTerm>නඩුව</SrcTerm> was taken up for further Trial<SrcTerm>නඩු විභාගය</SrcTerm> and concluded. An application was made on 2014.10.20 to call an additional Witness<SrcTerm>සාක්‍ෂිකරු</SrcTerm> on behalf of the Defence<SrcTerm>විත්තිය</SrcTerm>, and the Successor Judge<SrcTerm>අනුගාමී විනිසුරු තුමා</SrcTerm> has not granted permission. Accordingly, upon the Evidence<SrcTerm>සාක්‍ෂි</SrcTerm> being concluded before me, I have been empowered to prepare the Judgment<SrcTerm>තීන්දුව</SrcTerm> by the letter of the Judicial Service Commission<SrcTerm>අධිකරණ සේවා කොමිෂන් සභාව</SrcTerm> dated 2016.04.04. The Case Record<SrcTerm>නඩු වාර්තාව</SrcTerm> was handed to me on 19th May 2017.
        </p>

        <p className="font-display text-14 md:text-15 leading-[1.8] text-pl-text-primary mb-4">
          The Plaintiff<SrcTerm>පැමිණිලිකරු</SrcTerm>, against the Defendant<SrcTerm>විත්තිකරු</SrcTerm>, through the Prayer of the Plaint<SrcTerm>පැමිණිල්ලේ ආයචනය</SrcTerm>, has prayed for Damages<SrcTerm>අලාභ</SrcTerm> of Rupees 500 million and the relevant Legal Interest<SrcTerm>නෛතික පොලිය</SrcTerm> until the said amount is paid. It is stated that the statements set out in <ParaRef>paragraphs 06 and 12</ParaRef> of the plaint were made by the Defendant on 21st May 2003 with the intention of bringing the Plaintiff into Disrepute<SrcTerm>අවමානය</SrcTerm> and Defamation<SrcTerm>අපහාසය</SrcTerm>. The said statements were published in newspaper publications marked <Exhibit>P-01A</Exhibit>, <Exhibit>P-02A</Exhibit>, and <Exhibit>P-03A</Exhibit> along with the plaint.
        </p>

        <p className="font-display text-14 md:text-15 leading-[1.8] text-pl-text-primary mb-4">
          The stance of the Defence is that this Case cannot be instituted and maintained against the Defendant. <Exhibit>P-01A</Exhibit>, <Exhibit>P-02A</Exhibit>, <Exhibit>P-03A</Exhibit> are said to be true in meaning and in fact, and to have been published for the public benefit, for which the Defendant is entitled to privilege; upon that, a Cause of Action<SrcTerm>නඩු නිමිත්තක්</SrcTerm> has not arisen. Furthermore, as stated in <ParaRef>paragraph 08</ParaRef> of the answer, the Defendant has a duty to inform the public of the Plaintiff's faults. The Defendant has prayed to reject the plaint or otherwise to Dismiss<SrcTerm>නිෂ්ප්‍රභා කරන</SrcTerm> it Subject to Costs<SrcTerm>ගාස්තුවට යටත් කර</SrcTerm>.
        </p>

        <p className="font-display text-14 md:text-15 leading-[1.8] text-pl-text-primary mb-4">
          During the Trial, the following facts were admitted. Namely,
        </p>

        <div className="space-y-3 font-display text-14 md:text-15 leading-[1.8] text-pl-text-primary mb-2 pl-1">
          <div className="flex">
            <ItemNum>01.</ItemNum>
            <span>The facts mentioned in <ParaRef>paragraphs 1, 2, 3, 5</ParaRef> of the plaint,</span>
          </div>
        </div>
      </div>
    </div>

    {/* Footer ribbon — feature checks */}
    <div className="border-t border-pl-border bg-pl-bg-subtle px-6 md:px-8 py-4 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 font-mono text-10 uppercase tracking-[0.18em] text-pl-text-tertiary">
      <span>
        <span className="text-pl-accent">●</span> Page tracked:{" "}
        <span className="text-pl-text-secondary">2 / 21</span>
      </span>
      <span>
        <span className="text-pl-accent">●</span> ¶ refs preserved:{" "}
        <span className="text-pl-text-secondary">06 · 08 · 12 · 01</span>
      </span>
      <span>
        <span className="text-pl-accent">●</span> Exhibits:{" "}
        <span className="text-pl-text-secondary">P-01A · P-02A · P-03A</span>
      </span>
      <span>
        <span className="text-pl-accent">●</span> Glossed in-line:{" "}
        <span className="text-pl-text-secondary">20 terms</span>
      </span>
    </div>
  </figure>
);

export default Translate;
